/**
 * Aba Performance da Portfolio Analytics: uma carteira onshore, dados do Warren One.
 *
 * Endpoint próprio, chamado só com a aba aberta: o One não atrasa a página nem a quebra
 * quando está fora (o erro fica dentro da aba). Período do gráfico na URL (`periodo`).
 */
import { Alert, Group, Loader, SegmentedControl, SimpleGrid, Stack, Text } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { formatarData } from '../../lib/formatos';

export interface DadosPerformanceCarteira {
  carteira: string;
  periodo: string;
  opcoes: { periodos: Record<string, string> };
  info?: string;
  nome_one?: string;
  inicio?: string | null;
  kpis?: KpiApi[];
  janelas?: TabelaApi;
  grafico?: GraficoApi | null;
  mensal?: TabelaApi;
}

const PERIODOS = [
  { value: 'ano', label: 'No ano' },
  { value: '12m', label: '12 meses' },
  { value: '24m', label: '24 meses' },
  { value: '36m', label: '36 meses' },
  { value: 'inicio', label: 'Desde o início' },
];

export function PerformanceCarteira({
  carteiras,
  periodo,
  aoMudarPeriodo,
}: {
  carteiras: string[];
  periodo: string;
  aoMudarPeriodo: (periodo: string) => void;
}) {
  const carteira = carteiras.length === 1 ? carteiras[0] : null;
  const consulta = useQuery({
    queryKey: ['carteiras.controle.performance', carteira, periodo],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosPerformanceCarteira>>(
        '/carteiras-administradas/controle-de-posicoes/performance',
        { carteira: carteira!, periodo },
        signal,
      ),
    enabled: carteira !== null,
    placeholderData: keepPreviousData,
  });

  if (!carteira) {
    return (
      <Alert color="blue">
        Selecione só uma carteira para ver a performance (são {carteiras.length} selecionadas).
      </Alert>
    );
  }
  if (consulta.isError) {
    return (
      <Alert color="red">
        {consulta.error instanceof ErroApi
          ? consulta.error.message
          : 'Não foi possível carregar a performance.'}
      </Alert>
    );
  }
  if (consulta.isPending) return <Loader />;
  const d = consulta.data.data;
  if (d.info) return <Alert color="blue">{d.info}</Alert>;
  const ate = formatarData(consulta.data.meta.dados_ate ?? null);

  return (
    <Stack>
      <Text size="sm" c="dimmed">
        {`${d.nome_one} no Warren One · início em ${formatarData(d.inicio ?? null)} · até a última posição, ${ate}. Onshore, contra CDI e Ibovespa.`}
      </Text>
      {d.kpis && (
        <SimpleGrid cols={{ base: 2, lg: 4 }}>
          {d.kpis.map((k) => (
            <Kpi key={k.rotulo} kpi={k} />
          ))}
        </SimpleGrid>
      )}
      {d.janelas && (
        <Secao
          titulo="Retorno por janela"
          legenda="% do CDI = retorno ÷ CDI. Janela vazia: maior que o histórico da carteira."
        >
          <TabelaDados tabela={d.janelas} alturaAutomatica fixarPrimeira />
        </Secao>
      )}
      <Group>
        <Text size="sm" fw={500}>
          Período do gráfico
        </Text>
        <SegmentedControl
          data={PERIODOS}
          value={periodo}
          onChange={aoMudarPeriodo}
          color="dourado"
        />
      </Group>
      {d.grafico ? (
        <Grafico grafico={d.grafico} />
      ) : (
        <Alert color="blue">Sem série acumulada no período.</Alert>
      )}
      {d.mensal && (
        <Secao
          titulo="Retorno mensal"
          legenda="Coluna Ano: meses do ano compostos. Mês corrente acumulado até a última posição."
        >
          <TabelaDados tabela={d.mensal} alturaAutomatica fixarPrimeira />
        </Secao>
      )}
    </Stack>
  );
}
